/**
 * Analytics - thin typed wrapper over the Plausible script, plus the loader
 * that decides whether the real script is fetched at all.
 *
 * index.html only defines the tiny `window.plausible` queue stub (so custom
 * events below never throw, even before the real script loads). The actual
 * Plausible script tag used to be hardcoded in index.html and loaded
 * unconditionally in every environment, including staging — which meant
 * staging page views were silently mixed into production's real analytics.
 * `loadPlausibleIfProduction` (staging spec §5.3 / Codex #12) fixes that:
 * `main.tsx` calls it with `import.meta.env.VITE_APP_ENV`, and the script is
 * only injected when that build-time value is exactly 'production'.
 *
 * We do NOT track pageviews from a trackEvent call — the injected script does
 * that itself (auto-tracks pageviews incl. SPA navigation, ignores localhost).
 * Per-race and V1-vs-V2 breakdowns come from the URL path (/v2/<slug>,
 * /live/<slug>) in Plausible's "Top Pages" report, so no custom pageview
 * props are needed. `trackEvent` below only exists to fire CUSTOM events with
 * a typed signature.
 *
 * window.plausible is defined synchronously by the inline stub in index.html,
 * so calls are safe even before the async script finishes loading — early
 * calls queue and flush once it loads. Optional-chaining guards the rare case
 * where the script is blocked entirely.
 */

/**
 * The Plausible site script for live.racecommand.run (site id pa-iQa52vYp-…).
 * If the Plausible embed snippet is ever regenerated, this is the one value
 * to update.
 */
export const PLAUSIBLE_SRC = 'https://plausible.io/js/pa-iQa52vYp-gAEAPWqIUuA-.js';

/**
 * Injects the real Plausible script tag into `doc`'s <head> — but only when
 * `appEnv` is exactly 'production'. Called once from `main.tsx` before the
 * app renders, with `import.meta.env.VITE_APP_ENV` (a build-time constant, so
 * a staging build never even contains the production branch's live URL as a
 * reachable code path at runtime — it's simply skipped).
 *
 * `doc` defaults to the real `document` but is injectable so tests can pass a
 * scratch `Document` and assert on it without polluting the real page head.
 */
export function loadPlausibleIfProduction(appEnv: string | undefined, doc: Document = document): void {
  if (appEnv !== 'production') return;
  const script = doc.createElement('script');
  script.async = true;
  script.src = PLAUSIBLE_SRC;
  doc.head.appendChild(script);
}

type PropValue = string | number | boolean;

declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, PropValue>; callback?: () => void }
    ) => void;
  }
}

/**
 * Fire a custom Plausible event. No-ops safely if the script is blocked.
 *
 * Example:
 *   trackEvent('livestream_click', { destination: url });
 */
export function trackEvent(name: string, props?: Record<string, PropValue>): void {
  window.plausible?.(name, props ? { props } : undefined);
}
